import { motion } from "framer-motion";
import { Gavel, Scale, AlertCircle } from "lucide-react";
import { CaseTicker } from "@/components/landing/CaseTicker";

const CASES = [
  {
    amount: "$750,000",
    date: "APR 2026",
    who: "Indian Oaks Apartments LTD / Russell Management — Georgia",
    what: "Disability discrimination: refused a ground-floor unit to a mobility-impaired child for 14 months despite availability.",
    extra: "Second-largest individual Fair Housing Act settlement in DOJ history — plus mandated new accommodation policies and federal reporting.",
  },
  {
    amount: "$510,000",
    date: "APR 2026",
    who: "Kentucky landlord Joseph E. Johnson",
    what: "Sexual harassment of female tenants, eviction threats, entering homes without permission.",
    extra: "Permanently barred from managing residential rental properties.",
  },
  {
    amount: "$82,500",
    date: "JUL 2026",
    who: "East Hampton Housing Authority — New York",
    what: "Disability discrimination: refused to allow a resident's emotional assistance dog.",
    extra: "Settlement plus corrective fair-housing policy requirements.",
  },
  {
    amount: "$50,000",
    date: "MAR 2026",
    who: "D Jones Properties LLC — Green Bay, Wisconsin",
    what: "Sexual harassment lawsuit settled with the DOJ.",
    extra: "Court-ordered fair-housing training and a two-year DOJ monitoring period.",
  },
  {
    amount: "$20,000",
    date: "JAN 2026",
    who: "Decoy RV Park",
    what: "Disability discrimination and retaliation against residents.",
    extra: "RV and manufactured-housing operators are squarely in the enforcement scope.",
  },
];

const HUD_LADDER = [
  { label: "First violation", amount: "$21,663" },
  { label: "Second within 5 years", amount: "$54,157" },
  { label: "Third within 7 years", amount: "$108,315" },
];

const reveal = {
  initial: { opacity: 0, y: 24 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-80px" },
  transition: { duration: 0.7, ease: [0.16, 1, 0.3, 1] },
};

export const CostOfNoTraining = () => (
  <section id="cost" className="relative pb-24 sm:pb-32 bg-bone text-[#11141A]" data-testid="cost-section">
    <CaseTicker />
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-24 sm:pt-32">
      <motion.div {...reveal} className="max-w-3xl mb-6">
        <p className="text-xs font-mono font-semibold uppercase tracking-[0.22em] text-sky-600 mb-4 flex items-center gap-3">
          <span className="inline-block h-px w-10 bg-cadmium" /> The Cost of No Training
        </p>
        <h2 className="font-cabinet text-2xl sm:text-3xl lg:text-4xl font-extrabold tracking-tight">
          Untrained staff don't send a warning.{" "}
          <span className="font-editorial italic font-normal text-sky-600">They send a settlement.</span>
        </h2>
        <p className="mt-5 text-base text-neutral-600 leading-relaxed">
          Real Fair Housing Act enforcement actions from the last six months. Read the penalties — then read what every
          single settlement required afterward: policies, monitoring, and mandatory training.
        </p>
      </motion.div>

      <motion.div {...reveal} className="flex flex-wrap items-center gap-x-8 gap-y-3 mb-12 text-xs font-mono text-neutral-500">
        <span className="flex items-center gap-2">
          <Scale size={13} className="text-sky-600" />
          DOJ Sexual Harassment in Housing Initiative: 52 lawsuits, $19M+ recovered
        </span>
        <span className="flex items-center gap-2">
          <AlertCircle size={13} className="text-sky-600" />
          Management companies held vicariously liable for employee actions
        </span>
      </motion.div>

      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
        {CASES.map((c, i) => (
          <motion.article
            key={c.who}
            {...reveal}
            transition={{ ...reveal.transition, delay: i * 0.08 }}
            data-testid={`cost-case-card-${i}`}
            className="rounded-md bg-white border border-neutral-200 p-6 shadow-sm hover:shadow-xl hover:-translate-y-1 transition-all duration-300 flex flex-col"
          >
            <div className="flex items-start justify-between mb-4">
              <p className="font-mono text-3xl sm:text-4xl font-semibold tracking-tight text-red-700">{c.amount}</p>
              <span className="rounded-sm bg-neutral-100 border border-neutral-200 px-2 py-1 text-[10px] font-mono font-semibold uppercase tracking-widest text-neutral-500">
                {c.date}
              </span>
            </div>
            <p className="text-sm font-semibold text-[#11141A] mb-2">{c.who}</p>
            <p className="text-sm text-neutral-600 leading-relaxed mb-3">{c.what}</p>
            <p className="mt-auto text-xs text-neutral-500 leading-relaxed border-t border-neutral-100 pt-3 flex gap-2">
              <Gavel size={12} className="mt-0.5 shrink-0 text-sky-600" />
              {c.extra}
            </p>
          </motion.article>
        ))}

        <motion.div
          {...reveal}
          transition={{ ...reveal.transition, delay: 0.4 }}
          data-testid="cost-hud-ladder"
          className="rounded-md bg-[#14294B] text-white p-6 flex flex-col"
        >
          <p className="text-[10px] font-mono font-semibold uppercase tracking-[0.2em] text-white/60 mb-4">
            HUD administrative penalties — per violation
          </p>
          <div className="space-y-3.5 flex-1">
            {HUD_LADDER.map((r) => (
              <div key={r.label} className="flex items-center justify-between border-b border-slate-200 pb-3">
                <span className="text-sm text-white/75">{r.label}</span>
                <span className="font-mono text-xl font-semibold">{r.amount}</span>
              </div>
            ))}
          </div>
          <p className="mt-5 text-xs text-white/60 leading-relaxed">
            DOJ and private litigation damages are uncapped — and settlements routinely add years of federal monitoring
            and mandatory training on top.
          </p>
        </motion.div>
      </div>

      <motion.div
        {...reveal}
        data-testid="cost-small-owners-panel"
        className="mt-14 rounded-md bg-white text-slate-900 p-7 sm:p-10 grid lg:grid-cols-12 gap-10 relative overflow-hidden"
      >
        <div className="lg:col-span-5">
          <p className="text-xs font-mono font-semibold uppercase tracking-[0.22em] text-sky-600 mb-4 flex items-center gap-3">
            <span className="inline-block h-px w-10 bg-cadmium" /> For Small Owners
          </p>
          <h3 className="font-cabinet text-xl sm:text-2xl lg:text-3xl font-extrabold tracking-tight">
            Big portfolios have compliance departments.{" "}
            <span className="font-editorial italic font-normal text-sky-600">You have a manager and a spreadsheet.</span>
          </h3>
          <p className="mt-5 text-sm sm:text-base text-muted-foreground leading-relaxed">
            If you own 1–10 communities, one untrained mistake isn't a line item — it's a bad year. Owners and managers
            are named personally in fair-housing actions, and the "small landlord" exemption is far narrower than most
            people think: it never covers advertising and disappears the moment a broker is involved.
          </p>
          <p className="mt-4 text-sm sm:text-base text-muted-foreground leading-relaxed">
            OPERA360 gives a 3-property owner the same training, walk, and audit infrastructure a 300-property operator
            runs — for less per month than one hour of a defense attorney.
          </p>
        </div>
        <div className="lg:col-span-7 grid sm:grid-cols-2 gap-4">
          {[
            { figure: "$27K–$110K", label: "Replacing one community manager", note: "50–200% of a $55K salary in recruiting, vacancy drag, and ramp time (SHRM / Gallup benchmarks)." },
            { figure: "$50K–$200K+", label: "Defending one fair-housing complaint", note: "Through ALJ hearing or federal court — and fee-shifting means you pay your own lawyers even when you win." },
            { figure: "Personal", label: "Owners are named individually", note: "In April 2026 a Kentucky landlord wasn't just fined — he was permanently barred from the rental industry." },
            { figure: "7+ days", label: "Per unit, per turn, in lost rent", note: "Untrained teams average 19-day make-readies against a 12-day benchmark. That's rent you never recover." },
          ].map((c, i) => (
            <div key={c.label} data-testid={`cost-small-owner-card-${i}`} className="rounded-md border border-slate-200 bg-slate-50 p-5 hover:border-cadmium/40 transition-colors duration-300">
              <p className="font-mono text-2xl font-semibold text-sky-600">{c.figure}</p>
              <p className="mt-2 text-sm font-semibold">{c.label}</p>
              <p className="mt-1.5 text-xs text-muted-foreground leading-relaxed">{c.note}</p>
            </div>
          ))}
        </div>
      </motion.div>

      <motion.p {...reveal} className="mt-10 text-[11px] font-mono uppercase tracking-wider text-neutral-400">
        Sources: U.S. Department of Justice, Civil Rights Division — Housing &amp; Civil Enforcement Section (justice.gov),
        Jan–Jul 2026 · 24 CFR Part 180 civil penalty schedule · SHRM &amp; Gallup turnover benchmarks · HUD FHEO complaint process
      </motion.p>
    </div>
  </section>
);
